import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ShoppingBag, ArrowRight, Check, Handshake, MapPin, Loader2 } from "lucide-react";
import { usedDarts, sponsors, media, mailto } from "../mock";
import { suppliesApi, resolveImage } from "../api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-zinc-300">
        {label} {required && <span className="text-[#ff2e4d]">*</span>}
      </Label>
      {children}
    </div>
  );
}

const inputCls =
  "bg-[#0f0f11] border-white/15 text-white placeholder:text-zinc-500 focus-visible:ring-[#ff2e4d]";

function CoachingForm() {
  const [exp, setExp] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const lines = [
      `Name: ${fd.get("name") || ""}`,
      `Email: ${fd.get("email") || ""}`,
      `Phone: ${fd.get("phone") || ""}`,
      `Experience: ${exp || ""}`,
      `Preferred day/time: ${fd.get("pref") || ""}`,
      ``,
      `${fd.get("notes") || ""}`,
    ].join("\n");
    window.location.href = `${mailto("Coaching Registration")}&body=${encodeURIComponent(lines)}`;
    toast.success("Opening your email…", {
      description: "Your coaching request is ready to send to Bulls on Parade Darts.",
    });
    e.target.reset();
    setExp("");
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Full name" required><Input name="name" required className={inputCls} placeholder="Your name" /></Field>
      <Field label="Email" required><Input name="email" required type="email" className={inputCls} placeholder="you@email.com" /></Field>
      <Field label="Phone"><Input name="phone" className={inputCls} placeholder="(000) 000-0000" /></Field>
      <Field label="Experience level" required>
        <Select value={exp} onValueChange={setExp} required>
          <SelectTrigger className={inputCls}><SelectValue placeholder="Select one" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="beginner">Beginner</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced / League</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Preferred day / time"><Input name="pref" className={inputCls} placeholder="e.g. Tuesday evenings" /></Field>
      <Field label="Anything else?"><Textarea name="notes" className={inputCls} rows={3} placeholder="Tell us about your goals" /></Field>
      <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors">
        Register for Coaching <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
}

function YouthForm() {
  const submit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const lines = [
      `Parent/guardian: ${fd.get("parent") || ""}`,
      `Child's name: ${fd.get("child") || ""}`,
      `Child's age: ${fd.get("age") || ""}`,
      `Email: ${fd.get("email") || ""}`,
      `Phone: ${fd.get("phone") || ""}`,
      `Preferred session: ${fd.get("session") || ""}`,
      ``,
      `${fd.get("notes") || ""}`,
    ].join("\n");
    window.location.href = `${mailto("Youth Academy Registration")}&body=${encodeURIComponent(lines)}`;
    toast.success("Opening your email…", {
      description: "Your Youth Academy request is ready to send to Bulls on Parade Darts.",
    });
    e.target.reset();
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Parent / guardian name" required><Input name="parent" required className={inputCls} placeholder="Full name" /></Field>
      <Field label="Child's name" required><Input name="child" required className={inputCls} placeholder="Child's name" /></Field>
      <Field label="Child's age" required><Input name="age" required type="number" min="4" max="18" className={inputCls} placeholder="Age" /></Field>
      <Field label="Email" required><Input name="email" required type="email" className={inputCls} placeholder="you@email.com" /></Field>
      <Field label="Phone"><Input name="phone" className={inputCls} placeholder="(000) 000-0000" /></Field>
      <Field label="Preferred session"><Input name="session" className={inputCls} placeholder="e.g. Saturday mornings" /></Field>
      <Field label="Anything we should know?"><Textarea name="notes" className={inputCls} rows={3} placeholder="Allergies, experience, etc." /></Field>
      <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors">
        Register for Youth Academy <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
}

function ProShop() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await suppliesApi.list();
      setItems(data);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))];
  const shown = active === "All" ? items : items.filter((i) => i.category === active);

  return (
    <section id="supplies" className="scroll-mt-28 border-t border-white/10 bg-[#0f0f11]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#ff2e4d]/15 text-[#ff2e4d]"><ShoppingBag className="h-5 w-5" /></span>
          <h2 className="font-display text-3xl font-bold text-white">Pro Shop</h2>
        </div>
        <p className="mt-4 max-w-2xl text-zinc-400">
          On display, not for checkout here — contact us and we’ll get your order sorted.
        </p>
        <p className="mt-2 text-sm text-zinc-500">Flights &amp; shafts in stock and ready to go, $3 per pack.</p>

        {categories.length > 2 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  active === c ? "bg-[#ff2e4d] text-white" : "border border-white/15 text-zinc-300 hover:bg-white/10"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="mt-16 flex items-center justify-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : shown.length === 0 ? (
          <p className="mt-12 text-sm text-zinc-500">No supplies listed yet — check back soon.</p>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {shown.map((s) => (
              <div key={s.id} className="bop-card overflow-hidden rounded-xl">
                <div className="aspect-square overflow-hidden bg-[#0a0a0c]">
                  {s.image_url ? (
                    <img src={resolveImage(s.image_url)} alt={s.name} loading="lazy" className="h-full w-full object-contain p-3 transition-transform duration-300 hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-zinc-700"><ShoppingBag className="h-8 w-8" /></div>
                  )}
                </div>
                <div className="p-3">
                  <h4 className="text-sm font-medium text-white leading-snug">{s.name}</h4>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#ff2e4d]">{s.price}</span>
                    <a href={mailto(`Supply Order — ${s.name}`)} className="text-xs font-semibold text-zinc-300 hover:text-white bop-link-underline">Contact</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-8 text-sm text-zinc-500">Don’t see what you need? Message us for the full range of flights, shafts, points &amp; accessories.</p>
      </div>
    </section>
  );
}

export default function Programs() {
  return (
    <main className="bop-vignette">
      {/* Header */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-10 lg:pt-20">
        <p className="eyebrow">Programs & Pro Shop</p>
        <h1 className="mt-4 font-display text-4xl font-extrabold text-white sm:text-5xl lg:text-6xl">
          Everything Bulls On Parade runs.
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-zinc-400">
          Coaching, a youth academy, gear on display, and the sponsors behind it all.
        </p>
      </section>

      {/* PRO SHOP */}
      <ProShop />

      {/* USED DARTS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-white">Used Darts</h2>
        <p className="mt-4 max-w-2xl text-zinc-400">
          Secondhand sets from our own throwers, priced to move. No checkout here either — contact the owner directly to buy.
        </p>
        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <div className="grid grid-cols-2 gap-4">
            {usedDarts.images.map((im, i) => (
              <div key={i} className={`bop-card overflow-hidden rounded-xl ${i === 0 ? "col-span-2" : ""}`}>
                <div className={`overflow-hidden bg-[#0a0a0c] ${i === 0 ? "aspect-[16/9]" : "aspect-square"}`}>
                  <img src={im.src} alt={im.caption} loading="lazy" className="h-full w-full object-cover" />
                </div>
                <div className="px-4 py-2.5 text-xs text-zinc-400">{im.caption}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-col justify-center">
            <span className="inline-flex w-fit items-center rounded-full border border-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-zinc-300">{usedDarts.tag}</span>
            <h3 className="mt-4 font-display text-2xl font-bold text-white">{usedDarts.title}</h3>
            <div className="mt-2 font-display text-3xl font-extrabold text-[#ff2e4d]">{usedDarts.price}</div>
            <p className="mt-4 text-zinc-400">{usedDarts.desc}</p>
            <ul className="mt-5 space-y-2.5">
              {usedDarts.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-zinc-300">
                  <Check className="mt-0.5 h-4 w-4 flex-none text-[#ff2e4d]" /> {b}
                </li>
              ))}
            </ul>
            <a href={mailto("Used Darts — Dark Finish Steel-Tip Set")} className="mt-7 inline-flex w-fit items-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors">
              Contact to buy <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* COACHING */}
      <section id="coaching" className="scroll-mt-28 border-t border-white/10 bg-[#0f0f11]">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:px-8 py-16 lg:grid-cols-2">
          <div>
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img src={media.coaching} alt="A hand gripping a steel-tip dart, aimed at a dartboard" className="h-[420px] w-full object-cover" />
            </div>
          </div>
          <div>
            <p className="eyebrow">Adult Coaching</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Sharpen your throw</h2>
            <p className="mt-4 text-zinc-400">
              Weekly small-group and one-on-one sessions covering grip, stance, scoring routes, and
              checkout strategy. Whether you’re new to the oche or grinding league nights, sessions are
              built around where you actually are.
            </p>
            <div className="mt-8 rounded-2xl border border-white/10 bg-[#0c0c0e] p-6">
              <CoachingForm />
            </div>
          </div>
        </div>
      </section>

      {/* YOUTH ACADEMY */}
      <section id="youth-academy" className="scroll-mt-28">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:px-8 py-16 lg:grid-cols-2">
          <div className="lg:order-2">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img src={media.youth} alt="A youth-height dartboard practice station in a welcoming academy space" className="h-[420px] w-full object-cover" />
            </div>
          </div>
          <div className="lg:order-1">
            <p className="eyebrow">Youth Darts Academy</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Building the next generation</h2>
            <p className="mt-4 text-zinc-400">
              A welcoming program for young throwers, run by coaches who make grip, counting, and
              sportsmanship stick early. Soft-tip gear provided during sessions, no experience required.
            </p>
            <div className="mt-8 rounded-2xl border border-white/10 bg-[#0f0f11] p-6">
              <YouthForm />
            </div>
          </div>
        </div>
      </section>

      {/* SPONSORS */}
      <section id="sponsors" className="scroll-mt-28 relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0">
          <img src={media.sponsorsVenue} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c0c0e] via-[#0c0c0e]/90 to-[#0c0c0e]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#ff2e4d]/40 bg-[#ff2e4d]/10 px-3 py-1 text-xs font-semibold text-[#ff2e4d]">
            <Handshake className="h-4 w-4" /> Sponsorship
          </span>
          <h2 className="mt-5 font-display text-4xl font-bold text-white">Become a sponsor</h2>
          <p className="mt-4 max-w-2xl text-lg text-zinc-300">
            Sponsors keep coaching accessible, youth sessions affordable, and Ontario darts on the map.
            In return, your business gets visibility across our content, events, and this hub — in front
            of an audience that shows up week after week.
          </p>
          <a href={mailto("Sponsorship enquiry")} className="mt-7 inline-flex items-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors">
            Get in touch about sponsorship <ArrowRight className="h-4 w-4" />
          </a>

          <div className="mt-14">
            <h3 className="font-display text-xl font-bold text-white">Our sponsors</h3>
            <p className="mt-2 text-sm text-zinc-400">The businesses backing Bulls On Parade’s coaching, youth academy, and content.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {sponsors.map((s) => (
                <div key={s.name} className="bop-card rounded-xl p-5">
                  <h4 className="font-display text-lg font-semibold text-white">{s.name}</h4>
                  {s.place && (
                    <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-zinc-400">
                      <MapPin className="h-3.5 w-3.5 text-[#ff2e4d]" /> {s.place}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
